





























































































































































import {
  defineComponent,
  PropType,
  computed,
  ref,
  onMounted,
  watch,
  ComputedRef,
} from '@vue/composition-api';
import {
  Word,
  DictionaryForm,
  FormSpelling,
  TextOccurrencesCountResponseItem,
  AppliedProperty,
  PreselectionProperty,
} from '@oare/types';
import FormDisplay from './components/Forms/FormDisplay.vue';
import EventBus, { ACTIONS } from '@/EventBus';
import EditWordDialog from '@/views/DictionaryWord/components/WordInfo/components/EditWordDialog/EditWordDialog.vue';
import AddFormDialog from './components/AddFormDialog.vue';
import WordGrammar from './components/WordGrammar/WordGrammar.vue';
import sl from '@/serviceLocator';
import useQueryParam from '@/hooks/useQueryParam';
import { spellingHtmlReading } from '@oare/oare';
import _ from 'lodash';
import PropertiesTree from '@/components/Properties/PropertiesTree.vue';

export default defineComponent({
  props: {
    updateWordInfo: {
      type: Function as PropType<(newWord: Word) => void>,
      required: true,
    },
    wordUuid: {
      type: String,
      required: true,
    },
    wordInfo: {
      type: Object as PropType<Word>,
      required: true,
    },
    uuidToHighlight: {
      type: String,
      default: null,
    },
    cursor: {
      type: Boolean,
      default: true,
    },
    allowEditing: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    FormDisplay,
    EditWordDialog,
    AddFormDialog,
    WordGrammar,
    PropertiesTree,
  },
  setup(props) {
    const store = sl.get('store');
    const actions = sl.get('globalActions');
    const server = sl.get('serverProxy');

    const editDialogForm = ref<DictionaryForm>();
    const editDialogSpelling = ref<FormSpelling>();
    const editDialogDiscourse = ref(false);
    const showSpellingDialog = ref(false);
    const addFormDialog = ref(false);
    const filterByProperties = ref(false);
    const editLemmaPropertiesDialog = ref(false);
    const appliedPropertiesForFiltering = ref<(string | null)[]>([]);
    const filteredForms = ref(props.wordInfo.forms);
    const filteredFormsByProperties = ref(props.wordInfo.forms);
    const spellingOccurrencesCounts = ref<TextOccurrencesCountResponseItem[]>(
      []
    );
    const spellingOccurrencesCountsLoading = ref(false);

    const searchQuery = useQueryParam('filter', '', true);

    const canAddForms = computed(() => store.hasPermission('ADD_FORM'));

    const updateForm = (index: number, form: DictionaryForm) => {
      const updatedForms = [...props.wordInfo.forms];
      updatedForms[index] = form;
      props.updateWordInfo({
        ...props.wordInfo,
        forms: updatedForms,
      });
    };

    const properties = ref<AppliedProperty[]>([]);
    const setProperties = (propertyList: AppliedProperty[]) => {
      properties.value = propertyList;
      appliedPropertiesForFiltering.value = propertyList.map(prop =>
        prop.valueRow ? prop.valueRow.name : null
      );
    };

    onMounted(async () => {
      EventBus.$on(
        ACTIONS.EDIT_WORD_DIALOG,
        (options: {
          form: DictionaryForm;
          spelling?: FormSpelling;
          allowDiscourseMode: boolean;
        }) => {
          editDialogForm.value = options.form;
          editDialogSpelling.value = options.spelling || undefined;
          editDialogDiscourse.value = options.allowDiscourseMode;
          showSpellingDialog.value = true;
        }
      );

      try {
        spellingOccurrencesCountsLoading.value = true;
        const spellingUuids = props.wordInfo.forms.flatMap(form =>
          form.spellings.map(spelling => spelling.uuid)
        );
        spellingOccurrencesCounts.value =
          await server.getSpellingOccurrencesCounts(spellingUuids);
      } catch (err) {
        actions.showErrorSnackbar(
          'Error loading spelling occurrence counts. Please try again.',
          err as Error
        );
      } finally {
        spellingOccurrencesCountsLoading.value = false;
      }
    });

    const selectForm = (form: DictionaryForm) => {
      editDialogForm.value = form;
    };

    const filterWithProps = () => {
      filterByProperties.value = true;
      filteredForms.value = filteredForms.value.filter(form => {
        return (
          properties.value
            .map(prop => (prop.valueRow ? prop.valueRow.uuid : null))
            .every(val =>
              form.properties.map(prop => prop.valueUuid).includes(val)
            ) &&
          properties.value
            .map(prop => prop.variableRow.uuid)
            .every(val =>
              form.properties.map(prop => prop.variableUuid).includes(val)
            ) &&
          properties.value
            .map(prop => prop.variableRow.level)
            .every(val => form.properties.map(prop => prop.level).includes(val))
        );
      });
      filteredFormsByProperties.value = filteredForms.value;
    };

    const resetFilterByProperties = () => {
      filterByProperties.value = !filterByProperties.value;
      filteredForms.value = props.wordInfo.forms;
      filteredFormsByProperties.value = props.wordInfo.forms;
    };
    const filterSpellings = (
      form: DictionaryForm,
      searchHtmlReading: string
    ): DictionaryForm => {
      const filteredSpellings = form.spellings.filter(
        spelling =>
          spelling.spelling.includes(searchQuery.value) ||
          spellingHtmlReading(spelling.spelling).includes(searchHtmlReading)
      );
      return { ...form, spellings: filteredSpellings };
    };

    watch(
      () => searchQuery.value,
      _.debounce(() => {
        filteredForms.value = [];
        filteredFormsByProperties.value.map(form => {
          if (form.form.includes(searchQuery.value)) {
            filteredForms.value.push(form);
            return;
          }
          const searchHtmlReading = spellingHtmlReading(searchQuery.value);
          if (
            form.spellings.some(
              spelling =>
                spelling.spelling.includes(searchQuery.value) ||
                spellingHtmlReading(spelling.spelling).includes(
                  searchHtmlReading
                )
            )
          ) {
            const filteredForm = filterSpellings(form, searchHtmlReading);
            filteredForms.value.push(filteredForm);
          }
        });
      }, 500)
    );

    const getSpellingOccurrencesByForm = (
      form: DictionaryForm
    ): TextOccurrencesCountResponseItem[] => {
      return spellingOccurrencesCounts.value.filter(item =>
        form.spellings.map(spelling => spelling.uuid).includes(item.uuid)
      );
    };

    const partOfSpeechPreselects: ComputedRef<PreselectionProperty[]> =
      computed(() => {
        const posProperties = props.wordInfo.properties
          .filter(
            prop => prop.variableName === 'Part of Speech' && prop.valueUuid
          )
          .sort((a, b) => {
            if (!a.level) {
              return -1;
            } else if (!b.level) {
              return 1;
            }
            return a.level - b.level;
          });
        return posProperties.map(p => ({
          valueUuid: p.valueUuid!,
          variableHierarchyUuid: 'b74c7814-55f2-11eb-bf9e-024de1c1cc1d',
        }));
      });

    return {
      updateForm,
      editDialogForm,
      filterByProperties,
      editDialogSpelling,
      editDialogDiscourse,
      showSpellingDialog,
      addFormDialog,
      canAddForms,
      properties,
      editLemmaPropertiesDialog,
      appliedPropertiesForFiltering,
      searchQuery,
      filteredForms,
      selectForm,
      setProperties,
      resetFilterByProperties,
      filterWithProps,
      spellingOccurrencesCounts,
      getSpellingOccurrencesByForm,
      spellingOccurrencesCountsLoading,
      partOfSpeechPreselects,
    };
  },
});
